import React, { useEffect, useState } from "react";
import { Grid, Button, } from "@mui/material";
import { PieChart, Pie, Cell, Label, Tooltip, ResponsiveContainer, } from "recharts";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, } from "recharts";
import {
    ArrowLeftRight,
    CheckCircle2,
    Percent,
    XCircle,
} from "lucide-react";
import { getTransactionReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";
import ReportKpiCards from "./ReportKpiCards";
import {
    ChartLegend,
    ChartPanel,
    formatCompact,
    portalColor,
    rechartsAxis,
    rechartsGrid,
    ReportTooltip,
    RingCenter,
    statusColor,
} from "./reportCharts";

const drawerFilters = [
    {
        name: "portal",
        label: "Portal",
        type: "portal",
        defaultValue: "all",
        options: [
            { label: "All Portals", value: "all" },
            { label: "QBot", value: "qbot" },
            { label: "PayCentral", value: "paycentral" },
            { label: "KYC", value: "kyc" },
            { label: "Tripola", value: "tripola" },
        ],
    },
    {
        name: "status",
        label: "Status",
        type: "status",
        defaultValue: "all",
        options: [
            { label: "All Status", value: "all" },
            { label: "Success", value: "1" },
            { label: "Failed", value: "2" },
        ],
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const TransactionReport = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        portal: "all",
        status: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [summary, setSummary] = useState({
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        successRate: 0,
    });
    const [transactions, setTransactions] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const portalChartData = [
        { name: "QBot", value: transactions.filter((item) => item.portal === "QBot").length },
        { name: "PayCentral", value: transactions.filter((item) => item.portal === "PayCentral").length },
        { name: "KYC", value: transactions.filter((item) => item.portal === "KYC").length },
        { name: "Tripola", value: transactions.filter((item) => item.portal === "Tripola").length },
    ];

    const statusChartData = [
        { name: "Success", value: summary.successfulTransactions },
        { name: "Failed", value: summary.failedTransactions },
    ];

    const buildApiFilters = (fv) => ({
        portal: fv.portal === "all" ? "" : fv.portal,
        status: fv.status === "all" ? "" : fv.status,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getTransactionReportAction(buildApiFilters(fv));
            const reportData = response?.data?.data;
            setSummary(reportData?.summary || {});
            setTransactions(reportData?.transactions || []);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { portal: "all", status: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchReport(resetValues);
    };

    const hasAppliedFilters =
        filterValues.portal !== "all" ||
        filterValues.status !== "all";

    const statusLegend = statusChartData.map((item) => ({
        label: item.name,
        value: Number(item.value || 0),
        color: statusColor(item.name),
    }));

    const statusTotal = statusLegend.reduce((sum, item) => sum + item.value, 0);
    const portalTotal = portalChartData.reduce(
        (sum, item) => sum + Number(item.value || 0),
        0
    );

    return (
        <>
            <Grid container spacing={3}>
                {/* Filter Section */}
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">Transaction Report</h2>
                                </div>

                                {/* Buttons */}
                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                {/* Summary Cards */}
                <Grid item xs={12}>
                    <ReportKpiCards
                        loading={loading}
                        cards={[
                            {
                                key: "total",
                                label: "Total Transactions",
                                value: summary.totalTransactions ?? 0,
                                accent: "#22419e",
                                icon: <ArrowLeftRight />,
                            },
                            {
                                key: "success",
                                label: "Successful",
                                value: summary.successfulTransactions ?? 0,
                                accent: "#059669",
                                icon: <CheckCircle2 />,
                            },
                            {
                                key: "failed",
                                label: "Failed",
                                value: summary.failedTransactions ?? 0,
                                accent: "#dc2626",
                                icon: <XCircle />,
                            },
                            {
                                key: "rate",
                                label: "Success Rate",
                                value: `${summary.successRate ?? 0}%`,
                                accent: "#d97706",
                                icon: <Percent />,
                            },
                        ]}
                    />
                </Grid>

                {/* Charts */}
                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    {/* Status Chart */}
                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <ChartPanel
                            title="Transaction status"
                            caption="Successful against failed attempts"
                            height={330}
                            loading={loading}
                            isEmpty={statusTotal === 0}
                        >
                            <div className="report-ring">
                                <div className="report-ring-chart">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={statusChartData}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius="66%"
                                                outerRadius="92%"
                                                paddingAngle={2}
                                                cornerRadius={6}
                                                stroke="#ffffff"
                                                strokeWidth={2}
                                            >
                                                {statusChartData.map((entry) => (
                                                    <Cell
                                                        key={entry.name}
                                                        fill={statusColor(entry.name)}
                                                    />
                                                ))}
                                                <Label
                                                    position="center"
                                                    content={
                                                        <RingCenter
                                                            caption="Transactions"
                                                            value={formatCompact(statusTotal)}
                                                        />
                                                    }
                                                />
                                            </Pie>
                                            <Tooltip content={<ReportTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <ChartLegend items={statusLegend} />
                            </div>
                        </ChartPanel>
                    </Grid>

                    {/* Portal Chart */}
                    <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                        <ChartPanel
                            title="Transactions by portal"
                            caption="Volume processed per product"
                            height={330}
                            loading={loading}
                            isEmpty={portalTotal === 0}
                        >
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={portalChartData} barCategoryGap="34%">
                                    <CartesianGrid {...rechartsGrid} />
                                    <XAxis dataKey="name" {...rechartsAxis} />
                                    <YAxis
                                        {...rechartsAxis}
                                        width={44}
                                        tickFormatter={formatCompact}
                                    />
                                    <Tooltip
                                        content={<ReportTooltip />}
                                        cursor={{ fill: "rgba(34, 65, 158, 0.05)" }}
                                    />
                                    <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={44}>
                                        {portalChartData.map((entry) => (
                                            <Cell key={entry.name} fill={portalColor(entry.name)} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartPanel>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default TransactionReport;